import { useEffect, useRef, useState } from "react";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { AgentNodeEditor } from "src/agentNode/AgentNodeEditor";
import { AiNodeV2Editor } from "src/aiNode/AiNodeV2Editor";
import { Flow } from "src/api/flowTypes";
import { AssignmentNodeEditor } from "src/assignmentNode/AssignmentNodeEditor";
import { MergeView } from "src/base-components/CodeInput/MergeView";
import { EditorBoundsProvider } from "src/base-components/CodeInput/useEditorBounds";
import { CaseCreationNodeEditor } from "src/caseCreationNode/CaseCreationNodeEditor";
import { CaseUpdateNodeEditor } from "src/caseUpdateNode/CaseUpdateNodeEditor";
import { DiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { useDiffHighlighting } from "src/changeHistory/DiffViewModal/highlightDiffs";
import { WorkspaceDataplane } from "src/clients/flow-api";
import {
  AgentNode,
  AiNodeV2,
  AssignmentNode,
  BeMappedNode,
  CaseCreationNode,
  CaseUpdateNode,
  CustomConnectionNode,
  DatabaseConnectionNode,
  DecisionTableNode,
  FlowNode,
  InboundWebhookConnectionNode,
  IntegrationNode,
  LoopNode,
  ManifestConnectionNode,
  ManualReviewNode,
  MatrixTableNode,
  MLNode,
  NodeData,
  ReadEntityNode,
  RuleNodeV2,
  ScorecardNode,
  SimpleNode,
  SimpleNodeData,
  SplitNodeV2,
  WriteEntityNode,
} from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { CustomConnectionNodeEditor } from "src/customConnectionNode/Editor";
import { DatabaseConnecionNodeEditor } from "src/databaseConnectionNode/DatabaseConnecionNodeEditor";
import { DecisionTableNodeEditor } from "src/decisionTableNode/DecisionTableNodeEditor";
import { EmptyState } from "src/design-system/EmptyState";
import { ReadEntityNodeEditor } from "src/entityActionNodes/ReadEntityNodeEditor";
import { WriteEntityNodeEditor } from "src/entityActionNodes/WriteEntityNodeEditor";
import { InboundWebhookNodeEditor } from "src/inboundWebhookNode/InboundWebhookNodeEditor";
import { IntegrationNodeEditor } from "src/integrationNode/IntegrationNodeEditor";
import { ManifestConnectionNodeEditor } from "src/manifestConnectionNode/ManifestConnectionNodeEditor";
import { ManualReviewNodeEditor } from "src/manualReviewNode/ManualReviewNodeEditor";
import { MatrixTableNodeEditor } from "src/matrixTableNode/MatrixTableNodeEditor";
import { MLNodeEditor } from "src/mlNode/MLNodeEditor";
import { CodeNodeEditor } from "src/nodeEditor/CodeNodeEditor";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { FlowNodeEditor } from "src/parentFlowNodes/flowNode/FlowNodeEditor";
import { LoopNodeEditor } from "src/parentFlowNodes/loopNode/LoopNodeEditor";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { RuleV2Editor } from "src/ruleNodeV2Editor/RuleNodeV2Editor";
import { ScorecardNodeEditor } from "src/scorecardNode/ScorecardNodeEditor";
import { SplitNodeV2Editor } from "src/splitNodeV2Editor/SplitNodeV2Editor";
import { getCryptoRandomInt } from "src/utils/mathUtils";
import { assertUnreachable } from "src/utils/typeUtils";

const immutableBaseProps: NodeEditorBaseProps<NodeData> = {
  displayedError: undefined,
  immutable: true,
  isReactive: false,
  onUpdate: () => {},
};

const isMLNodeModelChanged = (original: MLNode, changed: MLNode): boolean => {
  return original.data.model_id !== changed.data.model_id;
};

export const shouldUseMergeView = (
  originalNode: BeMappedNode,
  changedNode: BeMappedNode,
): boolean => {
  // Code nodes always use merge view
  const isCodeNodeDiff =
    originalNode.type === NODE_TYPE.CODE_NODE &&
    changedNode.type === NODE_TYPE.CODE_NODE;

  // ML nodes use merge view only if the model wasn't changed (only src code changed)
  const isMlNodeSrcOnlyDiff =
    originalNode.type === NODE_TYPE.ML_NODE &&
    changedNode.type === NODE_TYPE.ML_NODE &&
    !isMLNodeModelChanged(originalNode as MLNode, changedNode as MLNode);

  return isCodeNodeDiff || isMlNodeSrcOnlyDiff;
};

const getImmutableNodeEditor = (
  node: BeMappedNode,
  flow: Flow,
  workspace: WorkspaceDataplane,
) => {
  switch (node.type) {
    case NODE_TYPE.AI_NODE_V2:
      return (
        <AiNodeV2Editor
          node={node as AiNodeV2}
          workspaceId={workspace.id}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.AGENT_NODE:
      return (
        <AgentNodeEditor
          selectedNode={node as AgentNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.CODE_NODE:
      return (
        <CodeNodeEditor
          src={(node as SimpleNode).data?.src}
          {...immutableBaseProps}
          language="python"
          onChange={() => {}}
        />
      );
    case NODE_TYPE.RULE_NODE_V2:
      return (
        <RuleV2Editor
          lazyInputs={false}
          selectedNode={node as RuleNodeV2}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.SPLIT_NODE_V2:
      return (
        <SplitNodeV2Editor
          selectedNode={node as SplitNodeV2}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.SCORECARD_NODE:
      return (
        <ScorecardNodeEditor
          selectedNode={node as ScorecardNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.ASSIGNMENT_NODE:
      return (
        <AssignmentNodeEditor
          lazyInputs={false}
          selectedNode={node as AssignmentNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.DECISION_TABLE_NODE:
      return (
        <DecisionTableNodeEditor
          isFullscreen={false}
          selectedNode={node as DecisionTableNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.MATRIX_TABLE_NODE:
      return (
        <MatrixTableNodeEditor
          isFullscreen={false}
          selectedNode={node as MatrixTableNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.INTEGRATION_NODE:
      return (
        <IntegrationNodeEditor
          node={node as IntegrationNode}
          workspaceId={workspace.id}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.MANIFEST_CONNECTION_NODE:
      return (
        <ManifestConnectionNodeEditor
          node={node as ManifestConnectionNode}
          workspaceId={workspace.id}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.ML_NODE:
      return (
        <MLNodeEditor
          flow={flow}
          selectedNode={node as MLNode}
          workspace={workspace}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.CUSTOM_CONNECTION_NODE:
      return (
        <CustomConnectionNodeEditor
          node={node as CustomConnectionNode}
          workspaceId={workspace.id}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.WEBHOOK_CONNECTION_NODE:
      return (
        <InboundWebhookNodeEditor
          node={node as InboundWebhookConnectionNode}
          workspaceId={workspace.id}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.SQL_DATABASE_CONNECTION_NODE:
      return (
        <DatabaseConnecionNodeEditor
          node={node as DatabaseConnectionNode}
          workspaceId={workspace.id}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.REVIEW_CONNECTION_NODE:
      return (
        <ManualReviewNodeEditor
          node={node as ManualReviewNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.CASE_CREATION_NODE:
      return (
        <CaseCreationNodeEditor
          node={node as CaseCreationNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.CASE_UPDATE_NODE:
      return (
        <CaseUpdateNodeEditor
          node={node as CaseUpdateNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.FLOW_NODE:
      return (
        <FlowNodeEditor
          lazyInputs={false}
          selectedNode={node as FlowNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.LOOP_NODE:
      return (
        <LoopNodeEditor
          lazyInputs={false}
          selectedNode={node as LoopNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.READ_ENTITY_NODE:
      return (
        <ReadEntityNodeEditor
          node={node as ReadEntityNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.WRITE_ENTITY_NODE:
      return (
        <WriteEntityNodeEditor
          node={node as WriteEntityNode}
          {...immutableBaseProps}
        />
      );
    case NODE_TYPE.SPLIT_MERGE_NODE:
    case NODE_TYPE.INPUT_NODE:
    case NODE_TYPE.OUTPUT_NODE:
    case NODE_TYPE.SPLIT_BRANCH_NODE_V2:
      return <></>;
    default:
      assertUnreachable(node.type);
      return null;
  }
};

type Props = {
  originalNode: BeMappedNode | "notYetCreated";
  changedNode: BeMappedNode | "deleted";
};

const inputPadding = 10;
export const editorWrapperClassName = "decideScrollbar w-full";

export const NodeDiffView: React.FC<Props> = ({
  originalNode,
  changedNode,
}) => {
  useDiffHighlighting("diff-view-before", "diff-view-after");
  const { workspace, flow } = useAuthoringContext();
  const prevContainerRef = useRef<HTMLDivElement>(null);
  const afterContainerRef = useRef<HTMLDivElement>(null);
  const maxXPrev = prevContainerRef.current
    ? prevContainerRef.current.getBoundingClientRect().x +
      prevContainerRef.current.clientWidth +
      inputPadding
    : null;
  const maxXAfter = afterContainerRef.current
    ? afterContainerRef.current.getBoundingClientRect().x +
      afterContainerRef.current.clientWidth +
      inputPadding
    : null;

  const [, forceRender] = useState(0);

  // force rendering is required due to field being used inside <Controller /> from react-hook-form
  // otherwise the controller component prevents re-rendering
  useEffect(() => {
    forceRender((_) => getCryptoRandomInt());
  }, [maxXPrev, maxXAfter]);

  return (
    <DiffViewContext.Provider
      value={{
        renderedInDiffView: true,
      }}
    >
      <div
        className="flex h-full min-h-0 w-full flex-1 items-stretch justify-between gap-x-6"
        data-loc="node-diff-view"
      >
        {/*For code nodes and if ml node only changed src code, we use the diff editor, for others we just render two editors*/}
        {originalNode !== "notYetCreated" &&
        changedNode !== "deleted" &&
        shouldUseMergeView(originalNode, changedNode) ? (
          <div className="h-150 w-full">
            <MergeView
              modified={(changedNode.data as SimpleNodeData).src}
              original={(originalNode.data as SimpleNodeData).src}
            />
          </div>
        ) : (
          <>
            <div
              ref={prevContainerRef}
              className={editorWrapperClassName}
              data-loc="node-diff-before"
            >
              <div className="h-full pb-4" id="diff-view-before">
                <EditorBoundsProvider maxX={maxXPrev}>
                  {originalNode === "notYetCreated" ? (
                    <EmptyState
                      description="This node was created and no changes have been applied"
                      headline="Node created"
                      icon={faPlus}
                    />
                  ) : (
                    getImmutableNodeEditor(originalNode, flow, workspace)
                  )}
                </EditorBoundsProvider>
              </div>
            </div>
            <div
              ref={afterContainerRef}
              className={editorWrapperClassName}
              data-loc="node-diff-after"
            >
              <div className="h-full pb-4" id="diff-view-after">
                <EditorBoundsProvider maxX={maxXAfter}>
                  {changedNode === "deleted" ? (
                    <EmptyState
                      description="This node was deleted, leaving no after state to display."
                      headline="Node deleted"
                      icon={faTrash}
                    />
                  ) : (
                    getImmutableNodeEditor(changedNode, flow, workspace)
                  )}
                </EditorBoundsProvider>
              </div>
            </div>
          </>
        )}
      </div>
    </DiffViewContext.Provider>
  );
};
